import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'

// IST Date helpers - DB dates are already in IST, parse directly without timezone conversion
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

// Client codes that determine the allowed relation type
const PARENT_CLIENT_CODE = 'FIB00001232C'
const PARENT_IN_LAW_CLIENT_CODE = 'FIB00001233C'

// Per-life premium table (Sum Insured → base premium excl. GST)
const PARENTAL_PREMIUM_TABLE: { sumInsured: number; premium: number }[] = [
  { sumInsured: 100000, premium: 10241 },
  { sumInsured: 200000, premium: 13948 },
  { sumInsured: 300000, premium: 18602 },
  { sumInsured: 500000, premium: 20598 },
  { sumInsured: 700000, premium: 22638 },
  { sumInsured: 1000000, premium: 23428 },
]

const getParentalPremium = (sumInsured: number): number => {
  const tier = PARENTAL_PREMIUM_TABLE.find((t) => t.sumInsured === sumInsured)
  return tier ? tier.premium : 0
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  policyCrmDbName?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  BaseSumInsured: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

const GENDERS = ['Male', 'Female', 'Other']
const GST_RATE = 0.18

export const WilliamsleaParentalEnrollmentForm: React.FC<Props> = ({
  policyData,
  policyEffectiveDate,
  policyExpiryDate,
  policyCrmDbName,
  onSuccess,
  preloadedMembers,
  initialDependents,
}) => {
  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Sum insured selection — employee picks the tier for parental coverage
  const [selectedSumInsured, setSelectedSumInsured] = useState<number>(PARENTAL_PREMIUM_TABLE[0].sumInsured)

  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: '',
  })

  const maxTotalDependents = 2

  // Determine allowed relation based on client code
  const getAllowedRelation = (): 'Parent' | 'ParentInLaw' => {
    const code = policyCrmDbName || policyData.CorporateCode || ''
    if (code === PARENT_IN_LAW_CLIENT_CODE) return 'ParentInLaw'
    return 'Parent'
  }

  const allowedRelation = getAllowedRelation()
  const allowedRelationLabel = allowedRelation === 'ParentInLaw' ? 'Parent-in-law' : 'Parent'

  // ─── Pro-rata helpers ───────────────────────────────────────────────────────
  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const getProrationFactor = (): number => {
    let dojRaw: any = undefined
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) { dojRaw = row.JoiningDate; break }
        if (row?.joiningdate != null) { dojRaw = row.joiningdate; break }
        if (row?.joiningDate != null) { dojRaw = row.joiningDate; break }
      }
    }
    if (dojRaw == null) dojRaw = policyData.DOJ

    const dojMs = parseDateOnlyToUtcMs(dojRaw)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()
  const isProrated = prorationFactor < 1
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (preloadedMembers && preloadedMembers.length > 0) {
      setEnrolledMembers(preloadedMembers)
      setLoadingFrozen(false)
      // Try to restore selected sum insured from first enrolled member
      const firstSI = preloadedMembers[0]?.BaseSumInsured
      if (firstSI && PARENTAL_PREMIUM_TABLE.some((t) => t.sumInsured === firstSI)) {
        setSelectedSumInsured(firstSI)
      }
    } else if (initialDependents && initialDependents.length > 0) {
      const mappedDependents = initialDependents
        .map((item, index) => {
          let relation = item.Relation
          const rLower = relation ? relation.toLowerCase() : ''
          if (['father', 'mother', 'parent'].includes(rLower)) relation = 'Parent'
          else if (rLower.includes('law')) relation = 'ParentInLaw'
          return {
            id: item.sid || Date.now() + index,
            name: item.Name,
            dob: item.DOB ? toISTInputFormat(item.DOB) : '',
            relation: relation,
            gender: item.Gender || '',
          }
        })
        .filter((dep) => dep.relation === 'Self' || dep.relation === 'Parent' || dep.relation === 'ParentInLaw')
      setDependents(mappedDependents)
    } else {
      setDependents([
        {
          id: 1,
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '',
        },
      ])
    }
  }, [policyData, preloadedMembers, initialDependents])

  const calculatePremium = () => {
    const parentDependents = dependents.filter((d) => d.relation !== 'Self')
    const dependentCount = parentDependents.length
    const perLifePremium = getParentalPremium(selectedSumInsured)

    const rawDependentPremium = perLifePremium * dependentCount
    const dependentPremium = Math.round(rawDependentPremium * prorationFactor)

    const dependentGST = Math.round(dependentPremium * GST_RATE)
    const dependentTotal = dependentPremium + dependentGST

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      gst: dependentGST,
      total: dependentTotal,
      perLifePremium,
    }
  }

  const handleAddClick = () => {
    setError(null)
    const parentDependents = dependents.filter((d) => d.relation !== 'Self')
    if (parentDependents.length >= maxTotalDependents) {
      setError(`Maximum of ${maxTotalDependents} ${allowedRelationLabel}s allowed.`)
      return
    }
    setNewDependent({ id: Date.now(), name: '', dob: '', relation: allowedRelation, gender: '' })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (dep.relation !== allowedRelation) {
      setError(`Only ${allowedRelationLabel} relation is allowed under this policy.`)
      return false
    }

    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    const currentParents = otherDeps.filter(
      (d) => d.relation === 'Parent' || d.relation === 'ParentInLaw'
    ).length

    if (currentParents >= maxTotalDependents) {
      setError(`Maximum ${maxTotalDependents} ${allowedRelationLabel}s allowed.`)
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return
    if (editingDependentId) {
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    setDependents(dependents.filter((d) => d.id !== id))
    setError(null)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return
    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()
    const perLifePremium = getParentalPremium(selectedSumInsured)

    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: dep.relation === 'Self' ? 0 : perLifePremium,
        Grade: policyData.Grade,
        crm_client_id: policyCrmDbName || policyData.CorpCode,
        Email: currentUser?.Email,
        CorporateCode: policyData.CorporateCode,
        SumInsured: selectedSumInsured,
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: premiumData.dependentCount > 0,
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,
          TopupName: '',
          topupsumInsured: 0,
          topup_base_premium: 0,
          topup_gst: 0,
          topup_total_premium: 0,
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      return baseMember
    })

    console.log('Submitting Williamslea Parental payload:', familyMembersWithPremium)

    try {
      const response = await fetch('https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(familyMembersWithPremium),
      })

      if (response.ok) {
        setShowSuccess(true)
      } else {
        setError('Failed to submit enrollment. Please try again.')
        setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()

  // ─── Frozen / Read-only view ────────────────────────────────────────────────
  if (policyData.freezeEnrolment) {
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0))
      return (
        <div className='text-center p-5 text-primary'>
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span> Loading
          enrollment details...
        </div>
      )

    const membersToDisplay =
      preloadedMembers && preloadedMembers.length > 0 ? preloadedMembers : enrolledMembers
    const frozenSI = membersToDisplay[0]?.BaseSumInsured || selectedSumInsured
    const frozenPremium = {
      dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
      gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
      total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    return (
      <div className='card'>
        <div className='card-body'>
          <Row>
            <Col md={8}>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0'>Enrolled {allowedRelationLabel} Details</h3>
                {frozenSI > 0 && (
                  <div className='badge badge-light-primary fs-6 p-3'>
                    Sum Insured: ₹{frozenSI.toLocaleString()}
                  </div>
                )}
              </div>
              <div className='table-responsive mb-5'>
                <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Date of Birth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToDisplay.map((member) => (
                      <tr key={member.Id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='text-dark fw-bolder fs-6'>{member.Name}</div>
                          </div>
                        </td>
                        <td>{member.Relation}</td>
                        <td>{member.Gender}</td>
                        <td>{formatDateIST(member.Dob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col md={4}>
              <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                <h4 className='mb-4'>Premium Summary</h4>
                {frozenPremium.dependentPremium > 0 && (
                  <div className='mb-3'>
                    <strong>{allowedRelationLabel} Premium:</strong>
                    <div className='float-end'>₹{frozenPremium.dependentPremium.toLocaleString()}</div>
                  </div>
                )}
                {frozenPremium.total > 0 ? (
                  <>
                    <hr />
                    <div className='mb-2'>
                      <span>Taxable Amount:</span>
                      <span className='float-end'>
                        ₹{frozenPremium.dependentPremium.toLocaleString()}
                      </span>
                    </div>
                    <div className='mb-2'>
                      <span>GST (18%):</span>
                      <span className='float-end'>₹{frozenPremium.gst.toLocaleString()}</span>
                    </div>
                    <hr />
                    <h5 className='text-primary'>
                      Total Payable:
                      <span className='float-end'>₹{frozenPremium.total.toLocaleString()}</span>
                    </h5>
                    <div className='text-muted fs-7 mt-2 text-end'>
                      The total premium will be deducted from your next salary.
                    </div>
                  </>
                ) : (
                  <div className='text-muted text-center py-5'>No additional premium applicable.</div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // ─── Success view ───────────────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <i className='bi bi-check-circle-fill text-success fs-3x'></i>
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>
          {premiumInfo.total > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of <strong>₹{premiumInfo.total.toLocaleString()}</strong> will be
                deducted from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ─── Active enrollment form ─────────────────────────────────────────────────
  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
          <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>{error}</div>
        )}
        <Row>
          <Col md={8}>
            {/* Dependent Table */}
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h3 className='mb-0'>{allowedRelationLabel} Details</h3>
                <div className='text-primary fw-bold mt-1'>
                  Sum Insured: ₹{selectedSumInsured.toLocaleString()} | Per-life Premium: ₹
                  {premiumInfo.perLifePremium.toLocaleString()}
                </div>
              </div>
              <Button
                variant='primary'
                onClick={handleAddClick}
                disabled={
                  dependents.filter((d) => d.relation !== 'Self').length >= maxTotalDependents
                }
              >
                Add {allowedRelationLabel}
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => (
                    <tr key={dep.id} className='text-capitalize text-dark'>
                      <td>
                        <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                          {dep.name}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.relation === 'ParentInLaw' ? 'Parent-in-law' : dep.relation}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.gender}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {formatDateIST(dep.dob)}
                        </span>
                      </td>
                      <td>
                        {dep.relation !== 'Self' && (
                          <div className='d-flex gap-2'>
                            <Button
                              variant='primary'
                              size='sm'
                              onClick={() => handleEditClick(dep)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              onClick={() => removeDependent(dep.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sum Insured Selection */}
            <div className='mb-5 mt-4'>
              <h4 className='mb-3'>Select Sum Insured for {allowedRelationLabel} Coverage</h4>
              <div className='alert alert-info d-flex align-items-center mb-3'>
                <i className='bi bi-info-circle-fill text-info fs-3 me-3'></i>
                <div className='text-gray-700 fw-bold fs-7'>
                  Premium is per-life (per {allowedRelationLabel}) based on the sum insured you
                  select. Maximum 2 {allowedRelationLabel}s can be enrolled.
                </div>
              </div>
              <Form.Group>
                {PARENTAL_PREMIUM_TABLE.map((tier) => (
                  <div
                    key={tier.sumInsured}
                    className={`p-3 mb-2 rounded border cursor-pointer ${
                      selectedSumInsured === tier.sumInsured
                        ? 'border-primary bg-light-primary'
                        : 'border-gray-300 bg-light'
                    }`}
                    onClick={() => setSelectedSumInsured(tier.sumInsured)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Form.Check
                      type='radio'
                      id={`si-${tier.sumInsured}`}
                      name='sumInsured'
                      checked={selectedSumInsured === tier.sumInsured}
                      onChange={() => setSelectedSumInsured(tier.sumInsured)}
                      label={
                        <span className='fw-bold ms-2'>
                          Sum Insured: ₹{tier.sumInsured.toLocaleString()} &nbsp;|&nbsp; Per-life
                          Premium: ₹{tier.premium.toLocaleString()}
                        </span>
                      }
                    />
                  </div>
                ))}
              </Form.Group>
            </div>
          </Col>

          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>

              {premiumInfo.dependentCount > 0 && (
                <div className='mb-3'>
                  <strong>
                    {allowedRelationLabel} ({premiumInfo.dependentCount}):
                  </strong>
                  <div className='float-end'>
                    ₹{premiumInfo.dependentPremium.toLocaleString()}
                  </div>
                </div>
              )}

              {isProrated && premiumInfo.dependentCount > 0 && (
                <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                  <span className='badge badge-warning me-2'>Pro-rated</span>
                  <span className='text-dark fs-7'>
                    Premium calculated for{' '}
                    <strong>{Math.round(prorationFactor * 365)} days</strong> remaining in the
                    policy year (factor: {(prorationFactor * 100).toFixed(1)}%).
                  </span>
                </div>
              )}

              {premiumInfo.dependentCount > 0 ? (
                <>
                  <hr />
                  <div className='mb-2'>
                    <span>Taxable Amount:</span>
                    <span className='float-end'>
                      ₹{premiumInfo.dependentPremium.toLocaleString()}
                    </span>
                  </div>
                  <div className='mb-2'>
                    <span>GST (18%):</span>
                    <span className='float-end'>₹{premiumInfo.gst.toLocaleString()}</span>
                  </div>
                  <hr />
                  <h5 className='text-primary'>
                    Total Payable:
                    <span className='float-end'>₹{premiumInfo.total.toLocaleString()}</span>
                  </h5>
                  <div className='text-muted fs-7 mt-2 text-end'>
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  Add a {allowedRelationLabel} to see the premium.
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button
                  variant='success'
                  size='lg'
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add / Edit Dependent Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setEditingDependentId(null)
          setError(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingDependentId ? `Edit ${allowedRelationLabel}` : `Add ${allowedRelationLabel}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
              {error}
            </div>
          )}
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={newDependent.name}
                onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Control
                type='text'
                value={allowedRelationLabel}
                disabled
                readOnly
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={newDependent.gender}
                onChange={(e) => setNewDependent({ ...newDependent, gender: e.target.value })}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type='date'
                value={newDependent.dob}
                onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowAddModal(false)
              setEditingDependentId(null)
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' onClick={saveDependent}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Enrollment Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning d-flex align-items-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-warning me-4'>
              <i className='bi bi-exclamation-triangle-fill fs-2'></i>
            </span>
            <div className='d-flex flex-column'>
              {premiumInfo.total > 0 && (
                <>
                  <div className='fw-bold mb-1'>
                    Total Premium Deduction: ₹{premiumInfo.total.toLocaleString()}
                  </div>
                  <div className='small'>This amount will be debited from your salary.</div>
                </>
              )}
              <div className='small mt-1'>
                <strong>
                  Please review and confirm your nominations carefully. Once submitted, no changes
                  can be made in the policy year.
                </strong>
              </div>
            </div>
          </div>
          <p className='mb-0 mt-3'>Do you want to proceed to enroll your {allowedRelationLabel}?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
